import { forwardRef, type InputHTMLAttributes, type ReactNode } from 'react';
import { cn } from '../../../lib/utils.js';

interface InputProps extends InputHTMLAttributes<HTMLInputElement> {
  label?: string;
  hint?: string;
  error?: string;
  leftIcon?: ReactNode;
  rightIcon?: ReactNode;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  ({ label, hint, error, leftIcon, rightIcon, className, id, ...props }, ref) => {
    const inputId = id ?? props.name;
    return (
      <div className="flex flex-col gap-1.5">
        {label && (
          <label htmlFor={inputId} className="text-sm font-medium text-[#0f172a]">
            {label}
          </label>
        )}
        <div className="relative">
          {leftIcon && (
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
              {leftIcon}
            </span>
          )}
          <input
            ref={ref}
            id={inputId}
            className={cn(
              'w-full rounded-lg border border-[#e2e8f0] bg-white px-3 py-2 text-sm text-[#0f172a]',
              'placeholder:text-[#94a3b8] transition-colors duration-150',
              'focus:outline-none focus:ring-2 focus:ring-[#0a4ee3]/20 focus:border-[#0a4ee3]',
              'disabled:bg-[#f8fafc] disabled:cursor-not-allowed',
              leftIcon && 'pl-10',
              rightIcon && 'pr-10',
              error && 'border-[#ef4444] focus:ring-[#ef4444]/20 focus:border-[#ef4444]',
              className,
            )}
            {...props}
          />
          {rightIcon && (
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[#94a3b8]">
              {rightIcon}
            </span>
          )}
        </div>
        {error ? (
          <p className="text-xs text-[#ef4444]">{error}</p>
        ) : hint ? (
          <p className="text-xs text-[#94a3b8]">{hint}</p>
        ) : null}
      </div>
    );
  },
);
Input.displayName = 'Input';
